import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../lib/api";

const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [requires2FA, setRequires2FA] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [requires2FASetup, setRequires2FASetup] = useState(false);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      const data = response.data;
      if (data.requires_2fa) {
        setRequires2FA(true);
        setTempToken(data.temp_token);
        return { requires2FA: true };
      }
      setToken(data.access_token);
      setUser(data.user);
      setIsAuthenticated(true);
      localStorage.setItem("rs_token", data.access_token);
      localStorage.setItem("rs_user", JSON.stringify(data.user));
      if (data.requires_2fa_setup) {
        setRequires2FASetup(true);
      }
      if (data.user?.must_change_password) {
        setRequiresPasswordChange(true);
        return { success: true, requiresPasswordChange: true };
      }
      return { success: true };
    } catch (e) {
      return { error: e.response?.data?.detail || "Login failed" };
    }
  };

  const verify2FA = async (code) => {
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        temp_token: tempToken, code
      });
      setToken(response.data.access_token);
      setUser(response.data.user);
      setIsAuthenticated(true);
      setRequires2FA(false);
      setTempToken(null);
      localStorage.setItem("rs_token", response.data.access_token);
      localStorage.setItem("rs_user", JSON.stringify(response.data.user));
      if (response.data.user?.must_change_password) {
        setRequiresPasswordChange(true);
        return { success: true, requiresPasswordChange: true };
      }
      return { success: true };
    } catch (e) {
      return { error: e.response?.data?.detail || "Invalid code" };
    }
  };

  const cancel2FA = () => {
    setRequires2FA(false);
    setTempToken(null);
  };

  const complete2FASetup = () => {
    setRequires2FASetup(false);
    // Update local user to reflect 2FA is now enabled
    const updatedUser = { ...user, totp_enabled: true };
    setUser(updatedUser);
    localStorage.setItem("rs_user", JSON.stringify(updatedUser));
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const authHeader = getAuthHeader();
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword, new_password: newPassword
      }, authHeader);
      const updatedUser = { ...user, must_change_password: false };
      setUser(updatedUser);
      setRequiresPasswordChange(false);
      localStorage.setItem("rs_user", JSON.stringify(updatedUser));
      return { success: true };
    } catch (e) {
      return { error: e.response?.data?.detail || "Password change failed" };
    }
  };

  const logout = () => {
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setRequiresPasswordChange(false);
    setRequires2FASetup(false);
    localStorage.removeItem("rs_token");
    localStorage.removeItem("rs_user");
  };

  const getAuthHeader = () => {
    const storedToken = token || localStorage.getItem("rs_token");
    if (storedToken) {
      return { headers: { Authorization: `Bearer ${storedToken}` } };
    }
    return {};
  };

  // On mount, check stored session and verify 2FA requirement
  useEffect(() => {
    const storedToken = localStorage.getItem("rs_token");
    const storedUser = localStorage.getItem("rs_user");
    if (storedToken && storedUser && storedUser !== "undefined" && storedUser !== "null") {
      let parsedUser;
      try {
        parsedUser = JSON.parse(storedUser);
      } catch (err) {
        // Corrupted localStorage — clear and bail so app can render login screen
        console.warn("Corrupted rs_user in localStorage, clearing session:", err);
        localStorage.removeItem("rs_token");
        localStorage.removeItem("rs_user");
        return;
      }
      if (!parsedUser || typeof parsedUser !== "object") {
        localStorage.removeItem("rs_token");
        localStorage.removeItem("rs_user");
        return;
      }
      setToken(storedToken);
      setUser(parsedUser);
      setIsAuthenticated(true);
      if (parsedUser?.must_change_password) {
        setRequiresPasswordChange(true);
      }
      // Check with server if 2FA setup is required
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${storedToken}` }
      }).then(res => {
        if (res.data?.requires_2fa_setup) {
          setRequires2FASetup(true);
        }
      }).catch(() => {
        // Token expired — force logout
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        localStorage.removeItem("rs_token");
        localStorage.removeItem("rs_user");
      });
    }
  }, []);

  return {
    isAuthenticated, user, login, logout, getAuthHeader,
    requires2FA, verify2FA, cancel2FA,
    requiresPasswordChange, changePassword,
    requires2FASetup, complete2FASetup
  };
};

export default useAuth;
